import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { Suspense } from 'react';
import { lazyWithReload } from '@/utils/lazyWithReload';
import RootLayout from '@/components/Layout/RootLayout';
import ComplianceLayout from '@/components/Layout/ComplianceLayout';
import AdminRoute from '@/components/Auth/AdminRoute';
import ReviewerRoute from '@/components/Auth/ReviewerRoute';
import AdminOrSpStaffRoute from '@/components/Auth/AdminOrSpStaffRoute';
import PermissionRoute from '@/components/Auth/PermissionRoute';
import FieldUserRoute from '@/components/Auth/FieldUserRoute';
import NonFieldUserRoute from '@/components/Auth/NonFieldUserRoute';
import { canViewServiceProviders, canAccessComplianceStatusLookup } from '@/utils/roleHelpers';
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';

// Lazy-loaded components for code splitting
const Dashboard = lazyWithReload(() => import('@/pages/Dashboard/Dashboard'));
const ComplianceManagementDashboard = lazyWithReload(() => import('@/pages/ComplianceManagement/ComplianceManagementDashboard'));
const ComplianceRequirementsView = lazyWithReload(() => import('@/pages/ComplianceManagement/ComplianceRequirementsView/ComplianceRequirementsView'));
const ComplianceReviewDashboard = lazyWithReload(() => import('@/pages/ComplianceManagement/ComplianceReviewDashboard/ComplianceReviewDashboard'));
const ComplianceRequirements = lazyWithReload(() => import('@/pages/ComplianceRequirements/ComplianceRequirements'));
const Profile = lazyWithReload(() => import('@/pages/Profile/Profile'));
const Settings = lazyWithReload(() => import('@/pages/Settings/Settings'));
const ServiceProviders = lazyWithReload(() => import('@/pages/ServiceProviders/ServiceProviderManagement'));
const ServiceProviderDetail = lazyWithReload(() => import('@/pages/ServiceProviders/pages/ServiceProviderDetail'));
const ServiceProviderUsers = lazyWithReload(() => import('@/pages/ServiceProviderUsers/ServiceProviderUserManagement'));
const InternalUsers = lazyWithReload(() => import('@/pages/InternalUsers/InternalUserManagement'));
const ComplianceStatusLookup = lazyWithReload(() => import('@/pages/ComplianceStatusLookup/ComplianceStatusLookup'));
const NotFound = lazyWithReload(() => import('@/pages/NotFound/NotFound'));
const Login = lazyWithReload(() => import('@/pages/Login/Login'));

// Higher-order component for wrapping components with Suspense and ErrorBoundary
const withAsyncBoundary = (Component: React.ComponentType) => {
  function WrappedComponent() {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Component />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Set display name for debugging
  WrappedComponent.displayName = `withAsyncBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

// Create wrapped components
const WrappedDashboard = withAsyncBoundary(Dashboard);
const WrappedComplianceManagementDashboard = withAsyncBoundary(ComplianceManagementDashboard);
const WrappedComplianceRequirementsView = withAsyncBoundary(ComplianceRequirementsView);
const WrappedComplianceReviewDashboard = withAsyncBoundary(ComplianceReviewDashboard);
const WrappedComplianceRequirements = withAsyncBoundary(ComplianceRequirements);
const WrappedProfile = withAsyncBoundary(Profile);
const WrappedSettings = withAsyncBoundary(Settings);
const WrappedServiceProviders = withAsyncBoundary(ServiceProviders);
const WrappedServiceProviderDetail = withAsyncBoundary(ServiceProviderDetail);
const WrappedServiceProviderUsers = withAsyncBoundary(ServiceProviderUsers);
const WrappedInternalUsers = withAsyncBoundary(InternalUsers);
const WrappedComplianceStatusLookup = withAsyncBoundary(ComplianceStatusLookup);

const ManageLegacyRedirect = () => {
  const { tab } = useParams<{ tab: string }>();
  return <Navigate to={`/compliance-status-lookup/${tab ?? 'drivers-monitors'}`} replace />;
};
const WrappedNotFound = withAsyncBoundary(NotFound);
const WrappedLogin = withAsyncBoundary(Login);

export const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: <WrappedLogin />,
    },
    {
      path: '/',
      element: <RootLayout />,
      errorElement: <WrappedNotFound />,
      children: [
        {
          index: true,
          element: <Navigate to="/dashboard" replace />,
        },
        {
          path: 'dashboard',
          element: <WrappedDashboard />,
        },
        {
          path: 'compliance-management',
          element: (
            <FieldUserRoute>
              <ComplianceLayout />
            </FieldUserRoute>
          ),
          children: [
            {
              index: true,
              element: <WrappedComplianceManagementDashboard />,
            },
            {
              path: 'requirements',
              element: <WrappedComplianceRequirementsView />,
            },
            {
              path: 'requirements/:sectionId',
              element: <WrappedComplianceRequirementsView />,
            },
            {
              path: 'compliance-requirements',
              element: <WrappedComplianceRequirementsView />,
            },
          ],
        },
        {
          path: 'compliance-review',
          element: (
            <ReviewerRoute>
              <WrappedComplianceReviewDashboard />
            </ReviewerRoute>
          ),
        },
        {
          path: 'compliance-requirements',
          element: (
            <NonFieldUserRoute>
              <Navigate to="/compliance-requirements/everdriven" replace />
            </NonFieldUserRoute>
          ),
        },
        {
          path: 'compliance-requirements/:tab',
          element: (
            <NonFieldUserRoute>
              <WrappedComplianceRequirements />
            </NonFieldUserRoute>
          ),
        },
        {
          path: 'profile',
          element: <WrappedProfile />,
        },
        {
          path: 'settings',
          element: <WrappedSettings />,
        },
        {
          path: 'service-providers',
          element: (
            <PermissionRoute check={canViewServiceProviders}>
              <WrappedServiceProviders />
            </PermissionRoute>
          ),
        },
        {
          path: 'service-providers/:id',
          element: (
            <PermissionRoute check={canViewServiceProviders}>
              <WrappedServiceProviderDetail />
            </PermissionRoute>
          ),
        },
        {
          path: 'service-provider-users',
          element: (
            <AdminOrSpStaffRoute>
              <WrappedServiceProviderUsers />
            </AdminOrSpStaffRoute>
          ),
        },
        {
          path: 'internal-users',
          element: (
            <AdminRoute>
              <WrappedInternalUsers />
            </AdminRoute>
          ),
        },
        {
          path: 'manage',
          element: <Navigate to="/compliance-status-lookup/drivers-monitors" replace />,
        },
        {
          path: 'manage/:tab',
          element: <ManageLegacyRedirect />,
        },
        {
          path: 'compliance-status-lookup',
          element: <Navigate to="/compliance-status-lookup/drivers-monitors" replace />,
        },
        {
          path: 'compliance-status-lookup/:tab',
          element: (
            <PermissionRoute check={canAccessComplianceStatusLookup}>
              <WrappedComplianceStatusLookup />
            </PermissionRoute>
          ),
        },
      ],
    },
    {
      path: '*',
      element: <WrappedNotFound />,
    },
  ]
);

// Route definitions for navigation menus and breadcrumbs
export const routes = {
  login: {
    path: '/login',
    label: 'Login',
  },
  dashboard: {
    path: '/dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
  },
  complianceManagement: {
    path: '/compliance-management',
    label: 'Compliance Management',
    icon: 'assessment',
  },
  complianceRequirements: {
    path: '/compliance-requirements',
    label: 'Compliance Requirements',
    icon: 'rule',
    children: {
      everdriven: {
        path: '/compliance-requirements/everdriven',
        label: 'EverDriven',
      },
      states: {
        path: '/compliance-requirements/states',
        label: 'States',
      },
      districts: {
        path: '/compliance-requirements/districts',
        label: 'Districts',
      },
    },
  },
  profile: {
    path: '/profile',
    label: 'Profile',
    icon: 'person',
  },
  settings: {
    path: '/settings',
    label: 'Settings',
    icon: 'settings',
  },
  serviceProviders: {
    path: '/service-providers',
    label: 'Service Providers',
    icon: 'business',
  },
  serviceProviderUsers: {
    path: '/service-provider-users',
    label: 'Service Provider Users',
    icon: 'people',
  },
  internalUsers: {
    path: '/internal-users',
    label: 'Internal Users',
    icon: 'admin_panel_settings',
  },
  manage: {
    path: '/manage',
    label: 'Manage',
    icon: 'directions_car',
  },
  complianceReview: {
    path: '/compliance-review',
    label: 'Compliance Review',
    icon: 'assignment_turned_in',
  },
} as const;

export type RouteKey = keyof typeof routes;